<template>
  <div class="working-dog">
    <div class="page-header">
      <h2><span class="breadcrumb-level">内容运营</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">服务板块管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">工作犬档案管理</span></h2>
      <div class="header-actions">
        <el-input v-model="searchKeyword" placeholder="搜索犬名..." clearable prefix-icon="Search" style="width:240px" @input="onSearchInput" />
        <el-button type="primary" @click="handleAdd">新增档案</el-button>
      </div>
    </div>

    <!-- 全局二维码 -->
    <div class="global-qrcode">
      <div class="global-qrcode-title">全局联系方式二维码</div>
      <div class="global-qrcode-body">
        <Upload v-model="globalQrUrl" :max-count="1" tip="推荐尺寸 宽 320 × 高 320 px" />
        <div class="global-qrcode-meta">
          <el-tag v-if="globalQrUrl" type="success" size="small">已设置</el-tag>
          <el-tag v-else type="info" size="small">未设置</el-tag>
          <span class="global-qrcode-hint">所有工作犬共享同一张二维码</span>
        </div>
        <el-button type="primary" size="small" @click="saveGlobalQrCode" :disabled="!globalQrUrl || saving" :loading="saving">保存二维码</el-button>
      </div>
    </div>

    <el-table :data="archives" stripe v-loading="loading">
      <el-table-column label="排序" width="80">
        <template #default="{ row }">
          <el-input-number v-model="row.sort_order" :min="0" size="small" @change="handleSortChange(row)" />
        </template>
      </el-table-column>
      <el-table-column label="封面" width="100">
        <template #default="{ row }">
          <el-image :src="row.cover_image" style="width:60px;height:60px;border-radius:4px" fit="cover" />
        </template>
      </el-table-column>
      <el-table-column label="名称" prop="title" min-width="150" />
      <el-table-column label="简介" prop="intro" min-width="200" show-overflow-tooltip />
      <el-table-column label="状态" width="100">
        <template #default="{ row }">
          <el-switch v-model="row.status" @change="handleStatusChange(row)" />
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button size="small" @click="handleEdit(row)">编辑</el-button>
          <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-if="pagination.total > 0"
      v-model:current-page="pagination.current_page"
      v-model:page-size="pagination.per_page"
      :total="pagination.total"
      layout="total, sizes, prev, pager, next, jumper"
      :page-sizes="[10,20,50]"
      small
      background
      style="margin-top:16px;justify-content:flex-end"
      @current-change="fetchData"
      @size-change="fetchData"
    />

    <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑档案' : '新增档案'" width="800px">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="120px">
        <el-form-item label="犬名" prop="title">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="封面图" prop="cover_image">
          <Upload v-model="form.cover_image" :max-count="1" tip="推荐尺寸 宽 294 × 高 200 px" />
        </el-form-item>
        <el-form-item label="详情轮播图">
          <Upload v-model="form.images" multiple :max-count="10" tip="推荐尺寸 宽 690 × 高 470 px" />
        </el-form-item>
        <el-form-item label="简介">
          <el-input v-model="form.intro" type="textarea" :rows="2" maxlength="500" />
        </el-form-item>
        <el-form-item label="详情内容">
          <RichTextEditor v-model="form.content" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort_order" :min="0" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import RichTextEditor from '../../components/RichTextEditor.vue';
import * as contentApi from '../../api/contentManagement';
import Upload from '../../components/Upload.vue';

const archives = ref([]);
const loading = ref(false);
const pagination = reactive({ current_page: 1, per_page: 20, total: 0, last_page: 1 });
const searchKeyword = ref('');
let searchTimer = null;
const onSearchInput = () => { clearTimeout(searchTimer); searchTimer = setTimeout(fetchData, 300); };
const dialogVisible = ref(false);
const isEdit = ref(false);
const formRef = ref(null);

const form = ref({
  title: '', cover_image: '', images: [], intro: '', content: '', sort_order: 0, status: true,
});

const rules = { title: [{ required: true, message: '请输入犬名', trigger: 'blur' }] };

// 全局二维码
const globalQrUrl = ref('');
const saving = ref(false);

const fetchData = async () => {
  loading.value = true;
  try {
    const params = { page: pagination.current_page, per_page: pagination.per_page };
    if (searchKeyword.value) params.search = searchKeyword.value;
    const res = await contentApi.getWorkingDogs(params);
    archives.value = res.data || [];
    pagination.current_page = res.pagination?.current_page || 1;
    pagination.total = res.pagination?.total || 0;
  } catch { ElMessage.error('获取列表失败'); } finally { loading.value = false; }
  // 同时加载全局二维码
  try {
    const r = await contentApi.getCompanyInfos();
    const data = r.data || {};
    globalQrUrl.value = (data.workdog_qrcode && data.workdog_qrcode.value) || '';
  } catch {}
};

const handleAdd = () => {
  isEdit.value = false;
  form.value = { title: '', cover_image: '', images: [], intro: '', content: '', sort_order: 0, status: true };
  dialogVisible.value = true;
};

const handleEdit = (row) => {
  isEdit.value = true;
  form.value = { ...row, images: row.images || [] };
  dialogVisible.value = true;
};

const handleSave = async () => {
  const valid = await formRef.value.validate().catch(() => false);
  if (!valid) return;
  try {
    if (isEdit.value) {
      await contentApi.updateWorkingDog(form.value.id, form.value);
      ElMessage.success('更新成功');
    } else {
      await contentApi.createWorkingDog(form.value);
      ElMessage.success('创建成功');
    }
    dialogVisible.value = false;
    await fetchData();
  } catch { ElMessage.error('操作失败'); }
};

const handleDelete = (row) => {
  ElMessageBox.confirm('确定删除？', '提示').then(async () => {
    await contentApi.deleteWorkingDog(row.id);
    ElMessage.success('删除成功');
    await fetchData();
  }).catch(() => {});
};

const handleSortChange = (row) => contentApi.updateWorkingDog(row.id, { sort_order: row.sort_order });
const handleStatusChange = (row) => contentApi.updateWorkingDog(row.id, { status: row.status });

const saveGlobalQrCode = async () => {
  if (!globalQrUrl.value || saving.value) return;
  saving.value = true;
  try {
    await contentApi.updateCompanyInfos({ workdog_qrcode: globalQrUrl.value });
    ElMessage.success('全局二维码已更新');
    await fetchData();
  } catch { ElMessage.error('保存失败'); } finally { saving.value = false; }
};

onMounted(fetchData);

</script>

<style scoped>
.header-actions { display: flex; gap: 12px; align-items: center; }
.global-qrcode{border:1px solid #e4e7ed;border-radius:8px;padding:16px 20px;margin-bottom:20px;background:#fafafa}
.global-qrcode-title{font-weight:600;font-size:15px;margin-bottom:12px;color:#303133}
.global-qrcode-body{display:flex;align-items:center;gap:16px;flex-wrap:wrap}
.global-qrcode-meta{display:flex;align-items:center;gap:8px}
.global-qrcode-hint{font-size:12px;color:#909399}
.upload-img { width: 200px; height: 120px; object-fit: cover; border-radius: 4px; }
.upload-icon { font-size: 28px; color: #8c939d; }
.avatar-uploader { border: 1px dashed #d9d9d9; border-radius: 6px; cursor: pointer; padding: 20px; text-align: center; }
.avatar-uploader:hover { border-color: #409eff; }
</style>
